import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Check, ChevronsUpDown, Building2, X } from "lucide-react";

/**
 * Searchable existing-account selector.
 * value = account id ("" for none). onChange(id, name) fires with the chosen
 * account (or "", "" when cleared). Lets a lead be a new prospect (no account)
 * or linked to a current customer account.
 */
export function AccountPicker({ value, onChange, testid = "account-picker" }) {
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    api.get("/accounts")
      .then((r) => { if (!cancel) setAccounts(Array.isArray(r.data) ? r.data : (r.data?.items || [])); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  const selected = useMemo(() => accounts.find((a) => a.id === value), [accounts, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={`${testid}-trigger`}
        >
          <span className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
            {selected ? <span className="truncate">{selected.name}</span> : <span className="text-slate-400">New prospect — no linked account</span>}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {selected && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onChange("", ""); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onChange("", ""); } }}
                className="text-slate-400 hover:text-slate-700"
                data-testid={`${testid}-clear`}
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search accounts…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No accounts found.</CommandEmpty>
            <CommandGroup>
              {accounts.map((a) => (
                <CommandItem
                  key={a.id}
                  value={a.name}
                  onSelect={() => { onChange(a.id, a.name); setOpen(false); }}
                  data-testid={`${testid}-option-${a.id}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === a.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{a.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
